"use client";

import { motion } from "motion/react";
import { createElement, type ElementType, type ReactNode } from "react";

// ScrollReveal — passthrough sans animation.
// Le texte (souvent avec data-blend) est rendu à son état final dès qu'il
// existe dans le DOM. Aucune fade-in, aucun slide, aucun whileInView. Quand
// l'utilisateur scrolle, le texte apparaît normalement dans le viewport
// avec mix-blend-mode déjà appliqué — pas d'effet d'"activation
// progressive" de l'inversion couleur.
export function ScrollReveal({
  children,
  className,
  as = "div",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: "div" | "section" | "article" | "header" | "li";
  amount?: number;
}) {
  return createElement(as as ElementType, { className }, children);
}

// ScrollStagger — wrapper container pour les grilles de cards. Toujours
// motion pour piloter le stagger sur les enfants (StaggerItem).
export function ScrollStagger({
  children,
  className,
  as = "div",
  staggerChildren = 0.08,
  delayChildren = 0,
  amount = 0.2,
}: {
  children: ReactNode;
  className?: string;
  as?: "div" | "section" | "article" | "header" | "ul" | "ol";
  staggerChildren?: number;
  delayChildren?: number;
  amount?: number;
}) {
  const MotionTag = motion[as];
  return (
    <MotionTag
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount }}
      transition={{ staggerChildren, delayChildren, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </MotionTag>
  );
}

// StaggerItem — slide-up subtil sans opacité (pour les cards qui ne sont
// PAS du texte data-blend). Si l'enfant a data-blend, préférer ScrollReveal
// passthrough ou pas d'animation du tout.
const slideUp = {
  hidden: { y: 16 },
  show: { y: 0 },
};

export function StaggerItem({
  children,
  className,
  as = "div",
}: {
  children: ReactNode;
  className?: string;
  as?: "div" | "article" | "li";
}) {
  const MotionTag = motion[as];
  return (
    <MotionTag
      variants={slideUp}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </MotionTag>
  );
}

export function CornerFrame({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`corner-frame ${className}`}>
      <span aria-hidden className="corner corner-tl" />
      <span aria-hidden className="corner corner-tr" />
      <span aria-hidden className="corner corner-bl" />
      <span aria-hidden className="corner corner-br" />
      {children}
    </div>
  );
}
